import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mirrorUser, setMirrorUserState] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('soem_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.clear();
      }
    }
    const storedMirror = localStorage.getItem('soem_mirror_user');
    if (storedMirror) {
      try {
        setMirrorUserState(JSON.parse(storedMirror));
      } catch {
        localStorage.removeItem('soem_mirror_user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('soem_access_token', access_token);
    localStorage.setItem('soem_refresh_token', refresh_token);
    localStorage.setItem('soem_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    ['soem_access_token', 'soem_refresh_token', 'soem_user', 'soem_mirror_user'].forEach(
      (k) => localStorage.removeItem(k)
    );
    setUser(null);
    setMirrorUserState(null);
  };

  const setMirrorUser = (mu) => {
    if (mu) {
      localStorage.setItem('soem_mirror_user', JSON.stringify(mu));
    } else {
      localStorage.removeItem('soem_mirror_user');
    }
    setMirrorUserState(mu);
  };

  const activeRole = mirrorUser?.role || user?.role;
  const activeUser = mirrorUser || user;

  const canAccess = (requiredRoles) => {
    if (!activeRole) return false;
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return requiredRoles.includes(activeRole);
  };

  const LIDER_ACTIONS = [
    'create_client', 'edit_client',
    'create_project', 'edit_project',
    'create_event', 'edit_event',
    'create_evaluation', 'edit_evaluation',
    'assign_levantador', 'assign_personal',
    'publish_evaluation', 'review_evaluation',
  ];

  const canDo = (action) => {
    if (activeRole === 'super_admin') return true;
    if (activeRole === 'lider_proyecto') return LIDER_ACTIONS.includes(action);
    return false;
  };

  const isAdmin = activeRole === 'super_admin';
  const isLider = activeRole === 'lider_proyecto' || activeRole === 'super_admin';
  const isLevantador =
    activeRole === 'levantador' || activeRole === 'lider_proyecto' || activeRole === 'super_admin';
  const isCliente = activeRole === 'cliente';

  return (
    <AuthContext.Provider
      value={{
        user,
        activeUser,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        mirrorUser,
        setMirrorUser,
        activeRole,
        canAccess,
        canDo,
        isAdmin,
        isLider,
        isLevantador,
        isCliente,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
